<template>
  <div class="input-wrapper">
    <!-- Label -->
    <label v-if="label" :for="inputId" :class="labelClasses">
      {{ label }}
    </label>
    
    <!-- Input Group -->
    <div :class="inputGroupClasses">
      <!-- Prefix -->
      <div v-if="$slots.prefix" class="input-prefix">
        <slot name="prefix"></slot>
      </div>
      
      <!-- Input Element -->
      <input
        :id="inputId"
        v-model="inputValue"
        :class="inputClasses"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :autocomplete="autocomplete"
        :aria-label="ariaLabel"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        @keydown="handleKeydown"
      />
      
      <!-- Suffix -->
      <div v-if="$slots.suffix || showClearButton" class="input-suffix">
        <!-- Clear Button -->
        <button
          v-if="showClearButton"
          type="button"
          class="input-clear"
          aria-label="清除輸入內容"
          @click="clearInput"
        >
          <i class="fas fa-times"></i>
        </button>
        
        <slot name="suffix"></slot>
      </div>
    </div>
    
    <!-- Help Text -->
    <div v-if="help && !error" class="input-help">
      {{ help }}
    </div>
    
    <!-- Error Message -->
    <div v-if="error" class="input-error">
      <i class="fas fa-exclamation-triangle error-icon"></i>
      {{ error }}
    </div>
    
    <!-- Success Message -->
    <div v-if="success" class="input-success">
      <span class="success-icon">✓</span>
      {{ success }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

interface Props {
  modelValue?: string | number
  type?: string
  label?: string
  placeholder?: string
  help?: string
  error?: string
  success?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  clearable?: boolean
  autocomplete?: string
  ariaLabel?: string
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  readonly: false,
  required: false,
  clearable: false,
  size: 'md'
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
  input: [event: Event]
  keydown: [event: KeyboardEvent]
  clear: []
}>()

const inputId = `input-${Math.random().toString(36).substr(2, 9)}`
const focused = ref(false)

const inputValue = computed({
  get: () => props.modelValue || '',
  set: (value) => emit('update:modelValue', value)
})

const labelClasses = computed(() => {
  const classes = ['input-label']
  
  if (props.required) {
    classes.push('input-label--required')
  }
  
  return classes
})

const inputGroupClasses = computed(() => {
  const classes = ['input-group']
  
  if (props.error) {
    classes.push('input-group--error')
  } else if (props.success) {
    classes.push('input-group--success')
  }
  
  if (focused.value) {
    classes.push('input-group--focused')
  }
  
  if (props.disabled) {
    classes.push('input-group--disabled')
  }
  
  return classes
})

const inputClasses = computed(() => {
  const classes = ['input']
  
  classes.push(`input--${props.size}`)
  
  if (props.error) {
    classes.push('input--error')
  } else if (props.success) {
    classes.push('input--success')
  }
  
  return classes
})

const showClearButton = computed(() => {
  return props.clearable && inputValue.value && !props.disabled && !props.readonly
})

const handleFocus = (event: FocusEvent) => {
  focused.value = true
  emit('focus', event)
}

const handleBlur = (event: FocusEvent) => {
  focused.value = false
  emit('blur', event)
}

const handleInput = (event: Event) => {
  emit('input', event)
}

const handleKeydown = (event: KeyboardEvent) => {
  emit('keydown', event)
}

const clearInput = () => {
  inputValue.value = ''
  emit('clear')
}
</script>

<style scoped>
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.input-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--c-gray-700);
}

.input-label--required::after {
  content: " *";
  color: var(--c-danger);
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
  border: 2px solid var(--c-border);
  border-radius: var(--radius-md);
  background-color: var(--c-bg);
  transition: all var(--transition-fast);
  overflow: hidden;
}

.input-group--focused {
  border-color: var(--c-primary);
  box-shadow: 0 0 0 4px rgba(85, 186, 189, 0.1);
}

.input-group--error {
  border-color: var(--c-danger);
}

.input-group--error.input-group--focused {
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
}

.input-group--success {
  border-color: var(--c-success);
}

.input-group--success.input-group--focused {
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.1);
}

.input-group--disabled {
  background-color: var(--c-gray-50);
  opacity: 0.7;
  cursor: not-allowed;
}

.input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--font-size-base);
  color: var(--c-fg);
  padding: var(--sp-3) var(--sp-4);
}

.input--sm {
  padding: var(--sp-2) var(--sp-3);
  font-size: var(--font-size-sm);
}

.input--lg {
  padding: var(--sp-4) var(--sp-6);
  font-size: var(--font-size-lg);
}

.input::placeholder {
  color: var(--c-gray-400);
}

.input:disabled {
  cursor: not-allowed;
}

.input-prefix,
.input-suffix {
  display: flex;
  align-items: center;
  padding: 0 var(--sp-3);
  color: var(--c-gray-500);
  font-size: var(--font-size-sm);
  background-color: var(--c-gray-50);
  border-color: var(--c-border);
}

.input-prefix {
  border-right: 1px solid var(--c-border);
}

.input-suffix {
  border-left: 1px solid var(--c-border);
}

.input-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: var(--c-gray-400);
  cursor: pointer;
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
}

.input-clear:hover {
  background-color: var(--c-gray-200);
  color: var(--c-gray-600);
}

.input-help {
  font-size: var(--font-size-xs);
  color: var(--c-gray-500);
}

.input-error {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  font-size: var(--font-size-sm);
  color: var(--c-danger);
}

.input-success {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  font-size: var(--font-size-sm);
  color: var(--c-success);
}

.error-icon,
.success-icon {
  font-size: var(--font-size-xs);
}
</style>
